import { useLocation } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { CircularProgress, Paper, Typography } from '@/ui';

import type { ReactNode } from 'react';

interface MemberBoundBookingProps {
  children: ReactNode;
}

const BoundMemberGate = ({
  children,
  memberId,
}: MemberBoundBookingProps & { memberId: string }) => {
  const { isUserLoading, user } = useCurrentUser();

  if (isUserLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100">
        <CircularProgress aria-label="Loading booking details" size={24} />
      </main>
    );
  }

  if (user.id !== memberId) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="max-w-md p-8 text-center" role="alert">
          <Typography variant="h5">
            This booking link was sent to a different member.
          </Typography>
        </Paper>
      </main>
    );
  }

  return children;
};

// MEN-5473: a coach or Ops copies a member booking link bound to one member
// with `?member=`. Anyone else who opens it sees a notice instead of booking
// UI, so no scheduling data loads for them. A link without the parameter is
// the member's own navigation and renders unchanged. The server still enforces
// the coach-member pairing; this only stops a forwarded link from booking.
const MemberBoundBooking = ({ children }: MemberBoundBookingProps) => {
  const { search } = useLocation();
  const memberId = new URLSearchParams(search).get('member');

  if (memberId === null) return children;

  return <BoundMemberGate memberId={memberId}>{children}</BoundMemberGate>;
};

export default MemberBoundBooking;
